import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';
import Lottie from 'lottie-react';

import scrollingBanks from 'src/assets/scrolling-banks.json';
import { Typography } from 'src/typography';
import { handleScroll } from 'src/utils/scroll';
import { AlertIcon } from 'src/assets/alert-icon';
import { WhatIsPayByBank } from 'src/payments/components/what-is-pbb';
import { getListSeparator } from 'src/checkout/components/sections/payment/shared/utils';
import { SearchIcon } from './search-icon';
import { usePayByBankSignup } from '../../../shared/use-pay-by-bank-signup';

export const HEADER_TEXT = 'Link your bank to pay instantly:';
export const CHANGE_ORDER_TYPE_TEXT = 'change your order type';
export const SEARCH_PLACEHOLDER_TEXT = 'Search 12k+ banks';
export const WHAT_IS_PBB_CTA_TEXT = 'Digital payments from 12k+ banks';

export const PayByBankSignup = observer(
  (): JSX.Element => {
    const {
      pbbSignupEnabledForOrderType,
      pbbOnlyEnabledForOtherOrderTypes,
      otherOrderTypesWithPBBEnabled,
      handleClick,
    } = usePayByBankSignup();

    if (!pbbSignupEnabledForOrderType && !pbbOnlyEnabledForOtherOrderTypes) {
      return <></>;
    }

    return (
      <SignupContainer>
        <Header size='medium' weight='semi'>
          {HEADER_TEXT}
        </Header>
        {pbbOnlyEnabledForOtherOrderTypes && (
          <BlurOverlay>
            <BlurBackground />
            <BlurContent>
              <AlertIcon />
              <Text size='medium'>'Pay now' is only available for</Text>
              <DeliveryTypes>
                {otherOrderTypesWithPBBEnabled.map((orderType, index) => {
                  const separator = getListSeparator(index, otherOrderTypesWithPBBEnabled.length);
                  return (
                    <Text size='medium' key={orderType}>
                      <strong>{orderType}</strong>
                      {separator}
                    </Text>
                  );
                })}
              </DeliveryTypes>
              <Text size='medium'>
                at this location,{' '}
                <HyperLink href='#orderType' onClick={handleScroll}>
                  {CHANGE_ORDER_TYPE_TEXT}
                </HyperLink>{' '}
                or select 'Pay in-store' to proceed.
              </Text>
            </BlurContent>
          </BlurOverlay>
        )}
        <SearchContainer>
          <SignupButtonContainer
            onClick={handleClick}
            type='button'
            aria-label='Search and link your bank to pay instantly'
            data-testid='dutchie-pay-signup-button'
          >
            <ButtonContent>
              <SearchIcon />
              <SearchText>{SEARCH_PLACEHOLDER_TEXT}</SearchText>
            </ButtonContent>
          </SignupButtonContainer>
        </SearchContainer>
        <BankContainerButton onClick={handleClick}>
          <Lottie
            animationData={scrollingBanks}
            style={{
              height: '48px',
              width: '100%',
            }}
            loop
            autoplay
          />
        </BankContainerButton>
        <Footer>
          <WhatIsPayByBank ctaText={WHAT_IS_PBB_CTA_TEXT} textDecoration={false} />
        </Footer>
      </SignupContainer>
    );
  }
);

const SignupContainer = styled.div`
  display: flex;
  position: relative;
  padding: 16px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[3]};
  align-self: stretch;
`;

const Header = styled(Typography.Body)`
  display: flex;
  padding: 0 16px;
  align-items: center;
  align-self: stretch;
  line-height: 165%;
`;

const SearchContainer = styled.div`
  display: flex;
  padding: 0 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const SearchText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
  color: #828a8f;
  font: 400 14px / 165% Matter;
`;

const BankContainerButton = styled.button`
  display: flex;
  height: 48px;
  width: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  font: inherit;
  border: none;
  background: none;
  padding: 0;
`;

const SignupButtonContainer = styled.button`
  display: flex;
  height: 48px;
  padding: 0 20px;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[4]};
  align-self: stretch;
  border-radius: ${({ theme: { customized } }) => customized.radius.sm};
  border: 1px solid #c1c7ca;
  background: #fcfdfe;
  cursor: pointer;
  font: inherit;

  &:hover {
    background: #f5f7f8;
    border-color: #a8b0b5;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
`;

const BaseBlurOverlay = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${({ theme: { spaces } }) => spaces[2]};
`;

const BlurOverlay = styled.div`
  ${BaseBlurOverlay}
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
  z-index: 10;
`;

const BlurBackground = styled.div`
  ${BaseBlurOverlay}
  background: #0b2034;
  backdrop-filter: blur(20px);
  opacity: 0.5;
  z-index: 9;
`;

const BlurContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  padding: ${({ theme: { spaces } }) => spaces[8]};
  justify-content: center;
  align-items: center;
  gap: 10px;
  z-index: 11;
`;

const Footer = styled.div`
  display: flex;
  padding: 0px 16px 8px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: center;
`;

const Text = styled(Typography.Body)`
  color: ${({ theme: { colors } }) => colors.white};
  text-align: center;
  line-height: 20px;
  & strong {
    font-weight: 700;
  }
`;

const HyperLink = styled.a`
  ${Text}
  text-decoration: underline;
  text-decoration-line: underline;
  cursor: pointer;
`;

const DeliveryTypes = styled.p`
  text-align: center;
`;
